import { type Pending, untilSaved } from "./retry";
import type { MandateDraft, PlanChangePreview, StorefrontPlan, SubscriptionOffer } from "./types";

export const MANAGE_TOKEN_KEY = "fg-recurring-manage-tokens";
const TOKEN_KEY = MANAGE_TOKEN_KEY;

function readTokens(): Record<string, string> {
  try {
    const raw = localStorage.getItem(TOKEN_KEY);
    const parsed = raw ? (JSON.parse(raw) as Record<string, string>) : {};
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

export function readManageToken(id: string): string {
  return readTokens()[id] || "";
}

function tokenIn(jar: Storage | null | undefined, id: string): string {
  if (!jar) return "";
  try {
    const raw = jar.getItem(TOKEN_KEY);
    const parsed = raw ? (JSON.parse(raw) as Record<string, string>) : {};
    const value = parsed && typeof parsed === "object" ? String(parsed[id] || "") : "";
    return value.trim();
  } catch {
    return "";
  }
}

/** Call from the click that sends the manage email, before any await. A granted iframe then
 * shares the Flowgate window's storage, so a later open of the email link shows up here. */
export function primeManageStorageAccess(): void {
  if (window.top === window.self) return;
  const request = (
    document as Document & {
      requestStorageAccess?: (types?: { localStorage?: boolean }) => Promise<unknown>;
    }
  ).requestStorageAccess?.bind(document);
  if (!request) return;
  const bounded = (pending: Promise<unknown>) =>
    Promise.race([pending, new Promise((resolve) => window.setTimeout(resolve, 1500))]);
  let pending: Promise<unknown>;
  try {
    pending = Promise.resolve(request({ localStorage: true }));
  } catch {
    try {
      void bounded(Promise.resolve(request()));
    } catch {
      /* this browser has no storage-access prompt */
    }
    return;
  }
  void bounded(pending).catch(() => {
    try {
      void request();
    } catch {
      /* ignore */
    }
  });
}

/** The email link saves the token in a normal Flowgate window. An embedded checkout has its own
 * storage, so a plan change there would keep sending the old token. Ask for the window's copy. */
export async function ensureManageToken(id: string): Promise<string> {
  const local = readManageToken(id);
  if (!id || window.top === window.self) return local;
  const shared = await readFirstPartyManageToken(id);
  if (shared && shared !== local) saveManageToken(id, shared);
  return shared || local;
}

async function readFirstPartyManageToken(id: string): Promise<string> {
  const request = (
    document as Document & {
      requestStorageAccess?: (types?: { localStorage?: boolean }) => Promise<{ localStorage?: Storage } | void>;
    }
  ).requestStorageAccess?.bind(document);
  if (!request) return "";
  try {
    const granted = await request({ localStorage: true });
    const jar = granted && typeof granted === "object" && granted.localStorage ? granted.localStorage : localStorage;
    return tokenIn(jar, id);
  } catch {
    try {
      await request();
      return tokenIn(localStorage, id);
    } catch {
      return "";
    }
  }
}

export function saveManageToken(id: string, token: string): void {
  const value = token.trim();
  if (!id || !value) return;
  const all = readTokens();
  all[id] = value;
  try {
    localStorage.setItem(TOKEN_KEY, JSON.stringify(all));
  } catch {
    /* private mode */
  }
}

export function forgetManageToken(id: string): void {
  if (!id) return;
  const all = readTokens();
  if (!all[id]) return;
  delete all[id];
  try {
    localStorage.setItem(TOKEN_KEY, JSON.stringify(all));
  } catch {
    /* private mode */
  }
}

function apiBase(): string {
  return (import.meta.env.VITE_API_BASE_URL || "").replace(/\/$/, "");
}

function url(path: string): string {
  return `${apiBase()}${path}`;
}

async function read<T>(response: Response): Promise<T> {
  const text = await response.text();
  let body: (T & { detail?: unknown; error?: string }) | null = null;
  try {
    body = text ? (JSON.parse(text) as T & { detail?: unknown; error?: string }) : null;
  } catch {
    throw new Error(text.trim().slice(0, 200) || `Request failed (${response.status})`);
  }
  if (!response.ok) {
    const detail = body?.detail;
    const msg =
      typeof detail === "string"
        ? detail
        : typeof detail === "object" && detail && "error" in (detail as object)
          ? String((detail as { error?: string }).error)
          : body?.error || `Request failed (${response.status})`;
    throw new Error(msg);
  }
  if (!body) {
    throw new Error(`Request failed (${response.status})`);
  }
  return body;
}

function authHeaders(id: string, json = false): HeadersInit {
  const headers: Record<string, string> = {};
  if (json) headers["Content-Type"] = "application/json";
  const token = readManageToken(id);
  if (token) headers["X-Manage-Token"] = token;
  return headers;
}

export async function fetchOffer(id: string): Promise<SubscriptionOffer> {
  const res = await fetch(url(`/v1/subscriptions/${encodeURIComponent(id)}`), {
    headers: authHeaders(id),
  });
  const body = await read<{ success: boolean; data: SubscriptionOffer }>(res);
  return body.data;
}

/** POST a save and repeat it while the server answers 202 (it is waiting for the network). */
async function save<T>(path: string, id: string, payload: unknown, onWaiting?: (step: string) => void): Promise<T> {
  return untilSaved<T>(
    async () => {
      const res = await fetch(url(path), {
        method: "POST",
        headers: authHeaders(id, true),
        body: JSON.stringify(payload),
      });
      if (res.status === 202) {
        const pending = (await res.json().catch(() => ({}))) as Pending;
        return { waiting: { pending: String(pending.pending || "network"), retry_after: pending.retry_after } };
      }
      const body = await read<{ success: boolean; data: T }>(res);
      return { done: body.data };
    },
    { onWaiting }
  );
}

export async function updateSpendingCap(
  id: string,
  amountRaw: string,
  approveTx?: string,
  onWaiting?: (step: string) => void
): Promise<SubscriptionOffer> {
  return save<SubscriptionOffer>(
    `/v1/subscriptions/${encodeURIComponent(id)}/spending-cap`,
    id,
    { amount_raw: amountRaw, approve_tx: approveTx },
    onWaiting
  );
}

/** Allowance the wallet's other live subscriptions on this token still need. */
export async function fetchWalletBudget(id: string, payer: string): Promise<bigint> {
  const params = new URLSearchParams({ payer });
  const res = await fetch(url(`/v1/subscriptions/${encodeURIComponent(id)}/wallet-budget?${params}`), {
    headers: authHeaders(id),
  });
  const body = await read<{ success: boolean; data: { others_raw: string } }>(res);
  return BigInt(body.data.others_raw || "0");
}

export function usesSignedTerms(offer: SubscriptionOffer): boolean {
  return offer.contract_version === 2;
}

/** connectAndApprove options for a v2 subscription: sign the terms, approve only when the allowance is short. */
export function signedTermsStep(
  offer: SubscriptionOffer,
  planId?: string
): { terms?: (payer: string) => Promise<MandateDraft>; approveIfNeeded?: boolean } {
  if (!usesSignedTerms(offer)) return {};
  return { terms: (payer) => fetchMandate(offer.subscription_id, payer, planId), approveIfNeeded: true };
}

/** Terms for this payer: activation, a plan change (with planId), or a new wallet. */
export async function fetchMandate(id: string, payer: string, planId?: string): Promise<MandateDraft> {
  const res = await fetch(url(`/v1/subscriptions/${encodeURIComponent(id)}/mandate`), {
    method: "POST",
    headers: authHeaders(id, true),
    body: JSON.stringify({ payer, plan_id: planId || undefined }),
  });
  const body = await read<{ success: boolean; data: MandateDraft }>(res);
  return body.data;
}

export async function replaceWallet(
  id: string,
  payer: string,
  approveTx?: string,
  mandateSignature?: string,
  onWaiting?: (step: string) => void
): Promise<SubscriptionOffer> {
  return save<SubscriptionOffer>(
    `/v1/subscriptions/${encodeURIComponent(id)}/wallet`,
    id,
    { payer, approve_tx: approveTx || undefined, mandate_signature: mandateSignature },
    onWaiting
  );
}

export async function changePlan(
  id: string,
  planId: string,
  preview = false,
  mandateSignature?: string
): Promise<SubscriptionOffer | PlanChangePreview> {
  try {
    // Signed terms may still be registering; the switch is repeated until they are on-chain.
    return await save<SubscriptionOffer | PlanChangePreview>(
      `/v1/subscriptions/${encodeURIComponent(id)}/plan`,
      id,
      { plan_id: planId, preview, mandate_signature: mandateSignature }
    );
  } catch (error) {
    if (error instanceof Error && error.message === "Manage token required") forgetManageToken(id);
    throw error;
  }
}

export async function requestManageLink(id: string): Promise<string> {
  const res = await fetch(url(`/v1/subscriptions/${encodeURIComponent(id)}/manage-link`), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
  });
  const body = await read<{ success: boolean; data: { masked_email: string } }>(res);
  return body.data.masked_email;
}

export async function manageLinkOpened(id: string): Promise<boolean> {
  const res = await fetch(url(`/v1/subscriptions/${encodeURIComponent(id)}/manage-link`));
  const body = await read<{ success: boolean; data: { opened: boolean } }>(res);
  return Boolean(body.data.opened);
}

export async function confirmFirstCharge(id: string): Promise<SubscriptionOffer> {
  const res = await fetch(url(`/v1/subscriptions/${encodeURIComponent(id)}/charge`), {
    method: "POST",
    headers: authHeaders(id, true),
  });
  const body = await read<{ success: boolean; data: SubscriptionOffer }>(res);
  return body.data;
}

export async function activateOffer(
  id: string,
  payer: string,
  approveTx?: string,
  mandateSignature?: string,
  onWaiting?: (step: string) => void
): Promise<SubscriptionOffer> {
  return save<SubscriptionOffer>(
    `/v1/subscriptions/${encodeURIComponent(id)}/activate`,
    id,
    { payer, approve_tx: approveTx || undefined, mandate_signature: mandateSignature },
    onWaiting
  );
}

export async function fetchStorefront(slug: string): Promise<StorefrontPlan> {
  const res = await fetch(url(`/v1/storefront/${encodeURIComponent(slug)}`));
  const body = await read<{ success: boolean; data: StorefrontPlan }>(res);
  return body.data;
}

export async function fetchStorefrontSubscription(
  slug: string,
  reference: string
): Promise<SubscriptionOffer | null> {
  const ref = reference.trim();
  if (!ref) return null;
  const params = new URLSearchParams({ reference: ref });
  const res = await fetch(url(`/v1/storefront/${encodeURIComponent(slug)}/subscription?${params}`));
  const body = await read<{ success: boolean; data: SubscriptionOffer | null }>(res);
  const offer = body.data || null;
  if (offer?.subscription_id && readManageToken(offer.subscription_id)) {
    return fetchOffer(offer.subscription_id);
  }
  return offer;
}

export async function retryOffer(id: string): Promise<SubscriptionOffer> {
  const res = await fetch(url(`/v1/subscriptions/${encodeURIComponent(id)}/retry`), {
    method: "POST",
    headers: authHeaders(id, true),
  });
  const body = await read<{ success: boolean; data: SubscriptionOffer }>(res);
  return body.data;
}

export async function cancelOffer(id: string): Promise<SubscriptionOffer> {
  const res = await fetch(url(`/v1/subscriptions/${encodeURIComponent(id)}/cancel`), {
    method: "POST",
    headers: authHeaders(id, true),
  });
  const body = await read<{ success: boolean; data: SubscriptionOffer }>(res);
  return body.data;
}

export async function subscribeStorefront(
  slug: string,
  assetId: string,
  contact: { name: string; email: string; reference?: string | null; planId?: string | null }
): Promise<SubscriptionOffer> {
  const res = await fetch(url(`/v1/storefront/${encodeURIComponent(slug)}/subscribe`), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      asset_id: assetId,
      customer_name: contact.name,
      customer_email: contact.email,
      reference: contact.reference || undefined,
      plan_id: contact.planId || undefined,
    }),
  });
  const body = await read<{ success: boolean; data: SubscriptionOffer }>(res);
  if (body.data.manage_token) saveManageToken(body.data.subscription_id, body.data.manage_token);
  return body.data;
}

export async function waitForSubscriptionStatus(
  id: string,
  done: (offer: SubscriptionOffer) => boolean,
  opts: { intervalMs?: number; timeoutMs?: number } = {}
): Promise<SubscriptionOffer> {
  const intervalMs = opts.intervalMs ?? 2000;
  const timeoutMs = opts.timeoutMs ?? 180_000;
  const started = Date.now();
  let offer = await fetchOffer(id);
  while (!done(offer)) {
    if (Date.now() - started > timeoutMs) {
      throw new Error("Timed out waiting for payment confirmation");
    }
    await new Promise((resolve) => window.setTimeout(resolve, intervalMs));
    offer = await fetchOffer(id);
  }
  return offer;
}
